import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Grid3x3 } from 'lucide-react';
import { DataTable } from '@/components/common/DataTable';
import { cn } from '@/lib/utils';
import type {
  PcmpCatalogItem,
  PcmpProjectDetail,
  PcmpProjectPrice,
} from '../types/pcmpProject.types';
import { formatBookPrice, pcmpPriceKey } from '../utils/pcmpPriceGrid';

interface PcmpPriceGridTableProps {
  project: Pick<PcmpProjectDetail, 'categories' | 'classes' | 'prices'>;
}

interface PriceGridRow {
  category: PcmpCatalogItem;
  pricesByClass: Map<number, PcmpProjectPrice>;
}

function getColumns(classes: PcmpCatalogItem[]): ColumnDef<PriceGridRow, unknown>[] {
  return [
    {
      id: 'category',
      header: 'Category',
      cell: ({ row }) => <span className="font-medium">{row.original.category.name}</span>,
    },
    ...classes.map(
      (academicClass): ColumnDef<PriceGridRow, unknown> => ({
        id: `class-${academicClass.id}`,
        header: () => <span className="block text-right">{academicClass.name}</span>,
        cell: ({ row }) => {
          const price = row.original.pricesByClass.get(academicClass.id);
          if (!price) return <span className="block text-right text-muted-foreground">—</span>;
          return (
            <span className={cn('block text-right tabular-nums', price.inUse && 'font-semibold')}>
              {formatBookPrice(price.bookPrice)}
              {price.inUse ? (
                <span
                  className="ml-1 inline-block size-1.5 rounded-full bg-primary align-middle"
                  aria-label="used by submissions"
                />
              ) : null}
            </span>
          );
        },
      }),
    ),
  ];
}

/** Read-only grid: a row per category, a column per class. */
export function PcmpPriceGridTable({ project }: PcmpPriceGridTableProps) {
  const rows = useMemo<PriceGridRow[]>(() => {
    const byKey = new Map(
      project.prices.map((price) => [pcmpPriceKey(price.categoryId, price.classId), price]),
    );
    return project.categories.map((category) => ({
      category,
      pricesByClass: new Map(
        project.classes.flatMap((academicClass) => {
          const price = byKey.get(pcmpPriceKey(category.id, academicClass.id));
          return price ? [[academicClass.id, price] as const] : [];
        }),
      ),
    }));
  }, [project]);
  const columns = useMemo(() => getColumns(project.classes), [project.classes]);
  const hasInUse = project.prices.some((price) => price.inUse);

  return (
    <div className="flex flex-col gap-2">
      <DataTable
        data={rows}
        columns={columns}
        stickyFirstColumn
        emptyIcon={Grid3x3}
        emptyMessage="This project has no prices yet."
      />
      {hasInUse ? (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="inline-block size-1.5 rounded-full bg-primary" aria-hidden />
          Used by submissions; these prices cannot be removed from the grid.
        </p>
      ) : null}
    </div>
  );
}
